import type { TutorialStep } from './tutorial-script';
import { t } from '../i18n/i18n';

/**
 * The plaque, and the light it throws on whatever it is talking about.
 *
 * ## It never takes control away
 *
 * The scrim is a shadow cast by a `pointer-events: none` element, not a lid
 * over the screen. Every pixel under it stays clickable, including the thing
 * being pointed at — which has to be true, because most steps end when the
 * player presses that exact thing. In the dungeon it matters more than that:
 * a plaque that swallowed input while a skeleton was mid-swing would be worse
 * than no tutorial at all. Only the plaque's own two buttons take a pointer.
 *
 * ## The hole is measured, not guessed
 *
 * `getBoundingClientRect` on every frame while a step is up, so the light
 * follows a tile through a scroll, a resize, a layout that reflows when a
 * panel opens. The loop only runs while a step with an anchor is showing, and
 * it writes nothing when the rect has not moved — a spotlight is not worth a
 * layout thrash on a mid-range phone.
 *
 * ## An anchor that is not there is not an error
 *
 * The plaque centres itself and says its piece anyway. A missing element
 * means the screen is narrower than the one this step was written against, or
 * the panel it lives in is closed — neither is a reason to withhold the
 * sentence that explains why death costs you the bag.
 */

/** Breathing room around the anchor, so the light frames it rather than tracing it. */
const HOLE_PAD = 8;
/** Gap between the anchor and the plaque. */
const GAP = 14;
/** Below this the plaque stops chasing anchors and sits at the bottom, where a thumb is. */
const SHEET_BREAKPOINT = 640;
const PLAQUE_W = 340;

/**
 * How dark the shadow around the light gets.
 *
 * Two values, because the two chapters are looked at under different
 * conditions. In the hub nothing is moving and the contrast is what makes the
 * plaque read. Underground the player may be mid-fight while a step is up, and
 * a shadow heavy enough to be handsome is heavy enough to lose a skeleton in —
 * so the delve chapter uses about half of it, which is still enough to lift the
 * bag or the action bar out of the scene.
 */
export const SCRIM = { hub: 0.66, delve: 0.34 } as const;

export interface CoachHandlers {
  /** The acknowledge button. Always present — see tutorial-script.ts. */
  onNext: () => void;
  /** Skip the whole tutorial, both chapters. */
  onSkip: () => void;
}

export interface CoachStepView {
  step: TutorialStep;
  index: number;
  total: number;
}

export class TutorialCoach {
  private readonly root: HTMLDivElement;
  private readonly hole: HTMLDivElement;
  private readonly plaque: HTMLDivElement;
  private readonly eyebrowEl: HTMLDivElement;
  private readonly titleEl: HTMLDivElement;
  private readonly bodyEl: HTMLParagraphElement;
  private readonly countEl: HTMLSpanElement;
  private readonly nextBtn: HTMLButtonElement;
  private readonly skipBtn: HTMLButtonElement;

  private anchorEl: Element | null = null;
  private rafHandle = 0;
  private lastRect = '';
  private visible = false;

  constructor(
    container: HTMLElement,
    private readonly handlers: CoachHandlers,
    private readonly dim: number = SCRIM.hub,
  ) {
    this.root = document.createElement('div');
    this.root.setAttribute('data-tutorial-coach', '');
    // Above the hub's panel overlay (z-40) and its toasts (z-2000): a step
    // that points at a tile has to stay legible once the tile's panel is up.
    this.root.className = 'fixed inset-0 z-[2600] pointer-events-none hidden';

    this.hole = document.createElement('div');
    this.hole.className = 'absolute rounded-xl pointer-events-none';
    this.hole.style.boxShadow = `0 0 0 9999px rgba(10, 7, 4, ${this.dim})`;
    this.hole.style.outline = '2px solid rgba(224, 178, 95, 0.75)';
    this.hole.style.outlineOffset = '0px';
    this.hole.style.transition = 'none';

    this.plaque = document.createElement('div');
    this.plaque.className = 'frame-pewter absolute pointer-events-auto p-4 flex flex-col gap-2 '
      + 'shadow-[0_18px_40px_rgba(0,0,0,0.55)]';
    this.plaque.setAttribute('role', 'dialog');
    this.plaque.setAttribute('aria-live', 'polite');
    this.plaque.setAttribute('aria-label', t('Tutorial'));

    this.eyebrowEl = document.createElement('div');
    this.eyebrowEl.className = 'eyebrow eyebrow-dim';

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'font-heading text-gold text-lg leading-tight';

    this.bodyEl = document.createElement('p');
    this.bodyEl.className = 'text-[13px] leading-relaxed text-text';

    const footer = document.createElement('div');
    footer.className = 'flex items-center gap-3 pt-1';

    this.countEl = document.createElement('span');
    this.countEl.className = 'text-[12px] tabular-nums text-text-dim mr-auto';

    this.skipBtn = document.createElement('button');
    this.skipBtn.type = 'button';
    // 44px floor, per CLAUDE.md. Skip is the control a player reaches for
    // fastest and the one they are angriest about missing.
    this.skipBtn.className = 'btn btn-quiet min-h-11 px-3 text-[13px]';
    this.skipBtn.textContent = t('Skip tutorial');
    this.skipBtn.addEventListener('click', () => this.handlers.onSkip());

    this.nextBtn = document.createElement('button');
    this.nextBtn.type = 'button';
    this.nextBtn.className = 'btn btn-gold min-h-11 px-4 text-[13px]';
    this.nextBtn.addEventListener('click', () => this.handlers.onNext());

    footer.append(this.countEl, this.skipBtn, this.nextBtn);
    this.plaque.append(this.eyebrowEl, this.titleEl, this.bodyEl, footer);
    this.root.append(this.hole, this.plaque);
    container.appendChild(this.root);
  }

  show(view: CoachStepView): void {
    const { step, index, total } = view;
    this.eyebrowEl.textContent = t(step.eyebrow);
    this.titleEl.textContent = t(step.title);
    this.bodyEl.textContent = t(step.body);
    this.countEl.textContent = t('{index} of {total}', { index, total });
    this.nextBtn.textContent = t(step.nextLabel);
    this.anchorEl = step.anchor ? document.querySelector(step.anchor) : null;

    this.visible = true;
    this.root.classList.remove('hidden');
    this.lastRect = '';
    this.bringAnchorIntoView();
    this.position();
    this.startTracking();
  }

  /**
   * Scrolls to the thing being talked about, when it is on the page but not on
   * the screen.
   *
   * Written for the phone, where the hub is a single tall column and the tile
   * a step points at is usually two screens down — without this, most of the
   * chapter would fall back to a sheet with nothing lit, which is a slideshow
   * again. Only ever scrolls to reach an anchor that exists; a step with no
   * anchor leaves the player exactly where they were.
   */
  private bringAnchorIntoView(): void {
    const el = this.anchorEl;
    if (!el || typeof el.scrollIntoView !== 'function') return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom > 0 && rect.top < window.innerHeight) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
  }

  hide(): void {
    this.visible = false;
    this.root.classList.add('hidden');
    this.stopTracking();
  }

  /** True while a step is on screen — the runner uses it to avoid stacking two. */
  get showing(): boolean { return this.visible; }

  private startTracking(): void {
    if (this.rafHandle || typeof requestAnimationFrame !== 'function') return;
    const tick = (): void => {
      if (!this.visible) { this.rafHandle = 0; return; }
      this.position();
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  private stopTracking(): void {
    if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
    this.rafHandle = 0;
  }

  /**
   * Places the light and the plaque against the anchor's current rect.
   *
   * Writes are skipped when the rect is unchanged, which is almost every
   * frame: the anchors here are hub tiles and a HUD panel, and both sit
   * still for whole steps at a time.
   */
  private position(): void {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const rect = this.anchorEl?.getBoundingClientRect();
    const key = rect ? `${rect.top}|${rect.left}|${rect.width}|${rect.height}|${vw}|${vh}` : `none|${vw}|${vh}`;
    if (key === this.lastRect) return;
    this.lastRect = key;

    // An anchor scrolled out of the viewport is treated as absent: a light
    // burning on an off-screen tile explains nothing and drags the plaque to
    // an edge with it.
    const onScreen = !!rect && rect.width > 0 && rect.height > 0
      && rect.bottom > 0 && rect.top < vh && rect.right > 0 && rect.left < vw;

    if (onScreen) {
      this.hole.style.display = '';
      this.hole.style.left = `${rect!.left - HOLE_PAD}px`;
      this.hole.style.top = `${rect!.top - HOLE_PAD}px`;
      this.hole.style.width = `${rect!.width + HOLE_PAD * 2}px`;
      this.hole.style.height = `${rect!.height + HOLE_PAD * 2}px`;
    } else {
      // No hole, but keep the scrim: the plaque needs contrast either way.
      // A zero-size box at the centre casts the same 9999px shadow.
      this.hole.style.display = '';
      this.hole.style.left = `${vw / 2}px`;
      this.hole.style.top = `${vh / 2}px`;
      this.hole.style.width = '0px';
      this.hole.style.height = '0px';
      this.hole.style.outline = 'none';
    }
    if (onScreen) this.hole.style.outline = '2px solid rgba(224, 178, 95, 0.75)';

    const sheet = vw < SHEET_BREAKPOINT;
    if (sheet || !onScreen) {
      // Bottom sheet on a phone, and whenever there is nothing to point at:
      // full width inside the safe area, where a thumb already is.
      this.plaque.style.left = sheet ? '0.75rem' : `${Math.round((vw - PLAQUE_W) / 2)}px`;
      this.plaque.style.right = sheet ? '0.75rem' : '';
      this.plaque.style.width = sheet ? 'auto' : `${PLAQUE_W}px`;
      this.plaque.style.top = '';
      this.plaque.style.bottom = sheet
        ? 'max(0.75rem, env(safe-area-inset-bottom))'
        : `${Math.round(vh * 0.12)}px`;
      return;
    }

    const width = Math.min(PLAQUE_W, vw - 24);
    this.plaque.style.width = `${width}px`;
    this.plaque.style.right = '';
    this.plaque.style.bottom = '';

    const height = this.plaque.offsetHeight || 160;
    const below = rect!.bottom + GAP;
    const above = rect!.top - GAP - height;
    // Below by default; above when below would run off the bottom and above
    // genuinely fits. Clamped either way, so a tall plaque beside a tall tile
    // lands on screen rather than half off it.
    const top = below + height <= vh - 12 ? below : (above >= 12 ? above : Math.max(12, vh - height - 12));
    const left = Math.min(Math.max(12, rect!.left + rect!.width / 2 - width / 2), vw - width - 12);

    this.plaque.style.top = `${Math.round(top)}px`;
    this.plaque.style.left = `${Math.round(left)}px`;
  }

  dispose(): void {
    this.stopTracking();
    this.root.remove();
  }
}
